import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ThumbsUp,
  ThumbsDown,
  ShieldCheck,
  Settings2,
  Heart,
  Check,
  AlertTriangle,
  Zap,
  Info,
  X,
  Wrench,
  Menu,
  Home,
  Globe,
  Sun,
  Moon,
  Coins,
} from 'lucide-react';
import { HealthCircle } from '../components/ui/HealthCircle';
import { useTranslations } from '../i18n';
import { usePreferences } from '../contexts/PreferencesContext';
import { calculateRepairAnalysis } from '../lib/repairAnalysis';
import { getFactorConfig, FUEL_TYPES_CONFIG, UNIT_TEMPLATES, VERDICT_RECOMMENDATIONS } from '../lib/repairAnalysisConfig';
import { formatCurrency } from '../lib/conversions';
import { supabase } from '../lib/supabase';
import { theme } from '../lib/theme';
import { NavigationBar } from '../components/ui/NavigationBar';
import { useAuth } from '../contexts/AuthContext';
import { LanguagePickerModal } from '../components/ui/LanguagePickerModal';
import { SupportedLanguage } from '../lib/expenseTypeHelpers';

type CarHealthViewProps = {
  car: any;
  logEntries: any[];
  isReadOnly?: boolean;
  onScoreUpdate?: (status: { score: number; recommendation: string }) => void;
  compact?: boolean;
};

export function CarHealthView({ car, logEntries, isReadOnly, onScoreUpdate, compact }: CarHealthViewProps) {
  const navigate = useNavigate();
  const { language, t } = useTranslations();
  
  const STORAGE_KEY = 'car_health_simulator_v2';
  const isSimulator = !car?.id || car?.id === 'simulator';

  // 1a. SIMULATOR SPECIFIC STATE
  const [simFuel, setSimFuel] = useState<string>('petrol');
  const [simYear, setSimYear] = useState<number>(new Date().getFullYear() - 5);
  const [simOwnership, setSimOwnership] = useState<number>(3);
  const [simMileage, setSimMileage] = useState<number>(150000);
  const [simServiceVisits, setSimServiceVisits] = useState<number>(1);
  const [simCurrency, setSimCurrency] = useState<string>('USD');
  const [simDistanceUnit, setSimDistanceUnit] = useState<string>('miles');

  // 1b. DATA INITIALIZATION
  const effectiveCar = useMemo(() => {
    if (!isSimulator) return car;
    return {
       id: 'simulator',
       fuel_type: simFuel,
       year: simYear,
       bought_at: new Date(new Date().getTime() - simOwnership * 365 * 24 * 60 * 60 * 1000).toISOString(),
       mileage: simMileage,
       currency: simCurrency,
       distance_unit: simDistanceUnit
    };
  }, [isSimulator, car, simFuel, simYear, simOwnership, simMileage, simCurrency, simDistanceUnit]);

  const baseAnalysis = useMemo(() => calculateRepairAnalysis(effectiveCar, logEntries), [effectiveCar, logEntries]);
  
  // 2. STATE (CONTROL CENTER)
  const persisted = car.repair_diagnosis_params_json || {};

  // Suggestions from history
  const suggestedKM = baseAnalysis.suggestedYearlyKM; 
  const suggestedRepairs = baseAnalysis.suggestedYearlyRepairs;
  const suggestedMarketValue = baseAnalysis.suggestedCarValue;

  const [plannedCost, setPlannedCost] = useState<number>(persisted.plannedCost ?? 0);
  const [yearlyKm, setYearlyKm] = useState<number | undefined>(persisted.yearlyKm);
  const [yearlyRepairCost, setYearlyRepairCost] = useState<number | undefined>(persisted.yearlyRepairCost);
  const [currentValue, setCurrentValue] = useState<number | undefined>(persisted.currentValue);
  
  const [q1, setQ1] = useState<number>(persisted.q1 ?? 3);
  const [q2, setQ2] = useState<number>(persisted.q2 ?? 3);
  const [q3, setQ3] = useState<number>(persisted.q3 ?? 3);
  const [q4, setQ4] = useState<number>(persisted.q4 ?? 3);
  const [nextCarPlan, setNextCarPlan] = useState<string>(persisted.nextCarPlan ?? 'none');
  const [nextCarBudget, setNextCarBudget] = useState<number>(persisted.nextCarBudget ?? 15000);
  const { } = useAuth();
  const { preferences: interfacePrefs, updatePreferences } = usePreferences();
  const [showMenu, setShowMenu] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [maintenanceDiscipline, setMaintenanceDiscipline] = useState<number>(Math.min(3, persisted.maintenanceDiscipline ?? 2));
  const [diyRepairs, setDiyRepairs] = useState<number>(Math.min(4, persisted.diyRepairs ?? 1));
  
  const [techRust, setTechRust] = useState<string>(persisted.techRust ?? 'none');
  const [techAccident, setTechAccident] = useState<string>(persisted.techAccident ?? 'none');
  const [techRepaint, setTechRepaint] = useState<string>(persisted.techRepaint ?? 'none');
  const [techUnresolved, setTechUnresolved] = useState<string>(persisted.techUnresolved ?? 'none');

  const carAge = effectiveCar.year ? (new Date().getFullYear()) - effectiveCar.year : null;
  const ownershipYears = effectiveCar.bought_at ? (new Date().getTime() - new Date(effectiveCar.bought_at).getTime()) / (1000 * 60 * 60 * 24 * 365) : null;

  const [activeInfo, setActiveInfo] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const currencySymbol = useMemo(() => {
    const mapper: any = { 'USD': '$', 'EUR': '€', 'GBP': '£', 'PLN': 'zł', 'SEK': 'kr', 'NOK': 'kr', 'CHF': 'CHF' };
    return mapper[isSimulator ? simCurrency : (car.currency || 'EUR')] || '€';
  }, [isSimulator, simCurrency, car.currency]);

  const unit = useMemo(() => {
    const distUnit = isSimulator ? simDistanceUnit : (car.distance_unit || 'km');
    return distUnit === 'miles' ? (language === 'lv' ? 'jūdzes' : 'miles') : 'km';
  }, [isSimulator, simDistanceUnit, car.distance_unit, language]);

  const unitFull = useMemo(() => {
    const distUnit = isSimulator ? simDistanceUnit : (car.distance_unit || 'km');
    return distUnit === 'miles' ? (language === 'lv' ? 'jūdžu' : 'miles') : 'km';
  }, [isSimulator, simDistanceUnit, car.distance_unit, language]);

  // Persistence logic
  const saveAllParams = async (params: any) => {
    if (isReadOnly) return;
    if (isSimulator) {
       localStorage.setItem(STORAGE_KEY, JSON.stringify({
          ...params,
          simFuel,
          simYear,
          simOwnership,
          simMileage,
          simServiceVisits,
          simCurrency,
          simDistanceUnit
       }));
       return;
    }

    try {
      setIsSaving(true);
      await supabase.from('cars').update({
        repair_diagnosis_params_json: params
      }).eq('id', car.id);
    } catch (e) {
      console.error("Diagnosis save failed:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let fresh: any = {};
    if (isSimulator) {
       const stored = localStorage.getItem(STORAGE_KEY);
       if (stored) {
          try {
             fresh = JSON.parse(stored);
             setSimFuel(fresh.simFuel ?? 'petrol');
             setSimYear(fresh.simYear ?? (new Date().getFullYear() - 5));
             setSimOwnership(fresh.simOwnership ?? 3);
             setSimMileage(fresh.simMileage ?? 150000);
             setSimServiceVisits(fresh.simServiceVisits ?? 1);
             setSimCurrency(fresh.simCurrency ?? 'USD');
             setSimDistanceUnit(fresh.simDistanceUnit ?? 'miles');
          } catch (e) { console.error("Sim load failed:", e); }
       }
    } else {
       fresh = car.repair_diagnosis_params_json || {};
    }

    if (fresh && Object.keys(fresh).length > 0) {
      setPlannedCost(fresh.plannedCost ?? 0);
      setYearlyKm(fresh.yearlyKm);
      setYearlyRepairCost(fresh.yearlyRepairCost);
      setCurrentValue(fresh.currentValue);
      setQ1(fresh.q1 ?? 3);
      setQ2(fresh.q2 ?? 3);
      setQ3(fresh.q3 ?? 3);
      setQ4(fresh.q4 ?? 3);
      setNextCarPlan(fresh.nextCarPlan ?? 'none');
      setNextCarBudget(fresh.nextCarBudget ?? 15000);
      setMaintenanceDiscipline(Math.min(3, fresh.maintenanceDiscipline ?? 2));
      setTechRust(fresh.techRust ?? 'none');
      setTechAccident(fresh.techAccident ?? 'none');
      setTechRepaint(fresh.techRepaint ?? 'none');
      setTechUnresolved(fresh.techUnresolved ?? 'none');
    }
    setIsLoaded(true);
  }, [car?.id, isSimulator]);

  useEffect(() => {
    if (!isLoaded) return;

    const params: any = {
      plannedCost,
      q1, q2, q3, q4, maintenanceDiscipline, diyRepairs,
      techRust, techAccident, techRepaint, techUnresolved,
      nextCarPlan, nextCarBudget
    };
    if (yearlyKm !== undefined) params.yearlyKm = yearlyKm;
    if (yearlyRepairCost !== undefined) params.yearlyRepairCost = yearlyRepairCost;
    if (currentValue !== undefined) params.currentValue = currentValue;

    if (isSimulator) {
      saveAllParams(params);
      return;
    }

    const timer = setTimeout(() => {
      saveAllParams(params);
    }, 1000);
    return () => clearTimeout(timer);
  }, [plannedCost, yearlyKm, yearlyRepairCost, currentValue, q1, q2, q3, q4, nextCarPlan, nextCarBudget, maintenanceDiscipline, diyRepairs, techRust, techAccident, techRepaint, techUnresolved, car?.id, isSimulator, simFuel, simYear, simOwnership, simMileage, simServiceVisits, isLoaded]);

  // 4. LOGIC
  
  const analysis = useMemo(() => {
    return calculateRepairAnalysis(effectiveCar, logEntries, {
      plannedRepairCost: plannedCost,
      yearlyKm,
      yearlyRepairCost,
      carValue: currentValue,
      q1, q2, q3,
      familyTripPriority: q4,
      nextCarPlan,
      nextCarBudget,
      rust: techRust,
      accident: techAccident,
      repaint: techRepaint,
      unresolved: techUnresolved,
      ownershipYears: isSimulator ? simOwnership : undefined,
      serviceVisits: isSimulator ? simServiceVisits : undefined,
      maintenanceDiscipline,
      diyRepairs,
      language
    });
  }, [effectiveCar, logEntries, isReadOnly, isSimulator, plannedCost, yearlyKm, yearlyRepairCost, currentValue, q1, q2, q3, q4, nextCarPlan, nextCarBudget, maintenanceDiscipline, diyRepairs, techRust, techAccident, techRepaint, techUnresolved, simOwnership, simServiceVisits, language]);

  useEffect(() => {
    if (onScoreUpdate) {
      onScoreUpdate({ score: analysis.score, recommendation: analysis.recommendation });
    }
  }, [analysis.score, analysis.recommendation, onScoreUpdate]);

  const fmt = (v: number) => formatCurrency(v, isSimulator ? simCurrency : (effectiveCar.currency || 'EUR'));

  // UI Components
  const LabelWithInfo = ({ id, label, icon: Icon, color }: any) => (
     <button 
        type="button"
        onClick={(e) => {
           e.preventDefault();
           e.stopPropagation();
           setActiveInfo(activeInfo === id ? null : id);
        }}
        className={`flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest transition-colors ${activeInfo === id ? 'text-blue-600' : 'text-gray-900 dark:text-white hover:text-blue-500'} pointer-events-auto cursor-pointer`}
     >
        {Icon && <Icon size={12} className={color} />}
        {label}
        <div className={`p-1 rounded-full ml-1 ${activeInfo === id ? 'text-blue-600 bg-blue-50/20' : 'text-blue-500'}`}>
           <Info size={14} />
        </div>
     </button>
  );

  const ScoreSelector = ({ value, label, id, icon: Icon, color, onChange }: any) => {
    const config = getFactorConfig(id);
    const labelFromConfig = (config?.name as any)?.[language] || label;
    const meaningsMap = config?.meanings || {};
    const hasKeys = Object.keys(meaningsMap).length > 0;
    const maxScore = hasKeys ? Object.keys(meaningsMap).length : 5;
    const items = hasKeys ? Object.keys(meaningsMap).map(Number).sort() : [1,2,3,4,5];
    
    return (
      <div className="space-y-1.5 relative">
         <div className="flex justify-between items-center mb-0.5">
            <LabelWithInfo id={id} label={labelFromConfig} icon={Icon} color={color} />
            <span className="text-gray-900 dark:text-white text-[12px] font-medium uppercase tracking-widest">
               {value}/{maxScore} — {(meaningsMap[value] as any)?.[language] || ""}
            </span>
         </div>
         <div className={`grid gap-1.5`} style={{ gridTemplateColumns: `repeat(${maxScore}, 1fr)` }}>
            {items.map(v => (
               <button 
                  key={v}
                  type="button"
                  onClick={() => onChange(v)}
                  className={`h-9 rounded-xl text-[10px] font-bold tracking-wider transition-all ${
                     value === v 
                        ? 'bg-blue-600 text-white shadow-sm ring-1 ring-blue-500/10' 
                        : 'bg-gray-100 dark:bg-slate-900 border border-gray-300 dark:border-white/20 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-800'
                  }`}
               >
                  {v}
               </button>
            ))}
         </div>
         {activeInfo === id && <InfoDialog id={id} />}
      </div>
    );
  };

  const TechSelector = ({ value, label, id, icon: Icon, color, options, onChange }: any) => {
    const config = getFactorConfig(id);
    const labelMap = config?.valueLabels || {};
    
    const labelFromConfig = (config?.name as any)?.[language] || label;
    
    return (
      <div className="space-y-1.5 relative">
         <div className="flex justify-between items-center mb-0.5">
            <LabelWithInfo id={id} label={labelFromConfig} icon={Icon} color={color} />
         </div>
         <div className={`grid gap-1.5`} style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
            {options.map((opt: any) => {
               const optId = typeof opt === 'string' ? opt : opt.id;
               const optLabel = typeof opt === 'string' ? (labelMap[optId] as any)?.[language] || optId : opt.label;
               const isSelected = value === optId;
               const bgMap: Record<string, string> = {
                   'text-amber-500': 'bg-amber-500',
                   'text-red-500': 'bg-red-500',
                   'text-indigo-500': 'bg-indigo-500',
                   'text-orange-600': 'bg-orange-600',
                };
                const bgClass = bgMap[color] || 'bg-blue-600';
                
                return (
                   <button 
                      key={optId}
                      type="button"
                      onClick={() => onChange(optId)}
                      className={`h-9 rounded-xl text-[10px] font-extrabold tracking-wider transition-all uppercase ${
                         isSelected 
                            ? `${bgClass} text-white shadow-sm ring-1 ring-inset ring-black/5` 
                            : 'bg-gray-100 dark:bg-slate-900 border border-gray-300 dark:border-white/20 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-800'
                      }`}
                   >
                     {optLabel}
                  </button>
               );
            })}
         </div>
         {activeInfo === id && <InfoDialog id={id} />}
      </div>
    );
  };
  const InfoDialog = ({ id }: { id: string }) => {
    const isSurvey = ['practical_fit', 'satisfaction', 'reliability_anxiety', 'family_priority'].includes(id);
    const config = getFactorConfig(id);
    const title = (config?.name as any)?.[language] || id;
    const description = (config?.description as any)?.[language] || "Information about this factor helps you make better decisions based on data and personal experience.";

    return (
      <div 
         onClick={() => setActiveInfo(null)}
         className={`absolute z-[100] ${isSurvey ? 'bottom-full mb-3' : 'top-full mt-2'} left-0 right-0 bg-white dark:bg-slate-900 border border-gray-100 dark:border-white/10 p-4 rounded-xl shadow-2xl animate-in fade-in zoom-in duration-200 pointer-events-auto min-w-[200px] cursor-pointer active:scale-[0.98] transition-all`}
      >
         <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2 text-blue-600">
               <Info size={14} />
               <span className="text-[10px] font-black uppercase tracking-widest">{title}</span>
            </div>
            <button type="button" onClick={() => setActiveInfo(null)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
         </div>
         <p className="text-[11px] font-medium leading-relaxed text-gray-600 dark:text-gray-300">
            {description}
         </p>
         <div className={`absolute left-4 ${isSurvey ? 'top-full -mt-1 border-t-white dark:border-t-slate-900' : 'bottom-full -mb-1 border-b-white dark:border-b-slate-900'} border-8 border-transparent w-0 h-0`} />
      </div>
    );
  };

  const SuggestedRow = ({ val, current, label, onSet, colorClass = "blue", extraDetails = {} }: any) => {
     const v = Number(val) || 0;
     const c = Number(current) || 0;
     const isDifferent = Math.abs(v - c) > 1;
     
     if (!v || !isDifferent) return null;
     
     const bgClass = colorClass === "green" ? "bg-green-100/50 dark:bg-green-400/10 border-green-200 dark:border-green-400/30" : "bg-blue-100/50 dark:bg-blue-400/10 border-blue-200 dark:border-blue-400/30";
     const textClass = colorClass === "green" ? "text-green-700 dark:text-green-300" : "text-blue-700 dark:text-blue-300";
     
     let finalLabel = label || "";
     const replacements: any = { 
        val: v.toLocaleString(), 
        km: v.toLocaleString(), 
        amount: v.toLocaleString(), 
        unit: unit || "km", 
        currencySymbol: currencySymbol || "€", 
        ...extraDetails 
     };
     
     Object.entries(replacements).forEach(([k, v]) => {
        finalLabel = finalLabel.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
     });

     return (
        <div className={`mt-3 flex items-center justify-between ${bgClass} px-3 py-2 rounded-xl border-2 shadow-sm animate-in fade-in slide-in-from-top-1 duration-300`}>
           <div className={`text-[11px] font-bold ${textClass} flex items-start gap-1.5 leading-tight`}>
              <Check size={14} className="shrink-0 mt-0.5" />
              <span>{finalLabel}</span>
           </div>
           <button 
              type="button"
              onClick={() => onSet(v)}
              className={`text-[10px] font-black ${textClass} uppercase bg-white dark:bg-slate-900 px-3 py-1.5 rounded-lg border-2 border-current hover:opacity-80 transition-opacity shrink-0`}
           >
              {t.repairAnalysis?.set || "Set"}
           </button>
        </div>
     );
  };

  const recommendation = analysis.recommendation || (VERDICT_RECOMMENDATIONS[analysis.verdict] as any)?.[language] || VERDICT_RECOMMENDATIONS[analysis.verdict]?.en;
  
  const SummaryBlocks = () => (
     <>
        {/* HEALTH SCORE HERO */}
        <div className={`bg-white dark:bg-slate-900/60 rounded-[2rem] border border-gray-100 dark:border-white/10 overflow-hidden p-0 mb-6 shadow-sm overflow-visible bg-gradient-to-br from-white to-gray-50/50 dark:from-slate-900 dark:to-slate-900/50`}>
            <div className={`${compact ? 'px-2 py-4' : 'p-4 sm:p-4 md:p-8'} flex flex-row items-center gap-1 md:gap-4`}>
              {/* Score Chart */}
              <HealthCircle 
                score={analysis.score} 
                size={128} 
                strokeWidth={15} 
                innerRadius={45} 
                showText={true} 
                fontSize="30px" 
              />

              <div className="flex-1 text-left min-w-0">
                  <div className="flex flex-row items-start justify-between gap-4 mb-4">
                    <div className="min-w-0 flex-1">
                       <h1 className={`font-black text-gray-900 dark:text-white tracking-tight mb-1 break-words leading-tight ${
                          (() => {
                             const name = isSimulator ? (language === 'lv' ? 'Auto simulators' : 'Car Simulator') : (car.make ? `${car.make} ${car.model}` : (car.name || ''));
                             return name.length > 20 ? 'text-lg md:text-2xl' : name.length > 15 ? 'text-xl md:text-2xl' : 'text-2xl';
                          })()
                       }`}>
                          {isSimulator ? (language === 'lv' ? 'Auto simulators' : 'Car Simulator') : (car.make ? `${car.make} ${car.model}` : car.name)}
                       </h1>
                        <div className="flex flex-wrap items-center justify-start gap-2">
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/5 rounded text-[10px] font-black uppercase tracking-widest text-gray-500">
                             {isSimulator ? simYear : car.year}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/5 rounded text-[10px] font-black uppercase tracking-widest text-gray-500">
                             {(FUEL_TYPES_CONFIG[isSimulator ? simFuel : car.fuel_type] as any)?.[language] || (isSimulator ? simFuel : car.fuel_type)}
                          </span>
                       </div>
                    </div>

                    <div className={`flex flex-col items-end gap-1 shrink-0 ${compact ? 'pr-2' : 'pr-2 sm:pr-0'}`}>
                       {carAge !== null && (
                          <div className="text-right">
                             <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{language === 'lv' ? 'Auto vecums' : 'Car Age'}</div>
                             <div className="text-sm font-black text-gray-900 dark:text-white">{carAge} {language === 'lv' ? (carAge === 1 ? 'gads' : 'gadi') : 'years'}</div>
                          </div>
                       )}
                       {ownershipYears !== null && (
                          <div className="text-right">
                             <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{language === 'lv' ? 'Īpašumā' : 'Owned for'}</div>
                             <div className="text-sm font-black text-gray-900 dark:text-white">{ownershipYears < 1 ? (language === 'lv' ? '< 1 gads' : '< 1 yr') : `${Math.floor(ownershipYears)} ${language === 'lv' ? 'gadus' : 'years'}`}</div>
                          </div>
                       )}
                    </div>
                 </div>
                 {isSaving && <div className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full" />}
              </div>
           </div>
        </div>

        {/* VERDICT CARD */}
        {!compact && (
           <div className={`bg-white dark:bg-slate-900/60 rounded-[2rem] border border-gray-100 dark:border-white/10 overflow-hidden ${compact ? 'px-4 py-6' : 'p-6 md:p-8'} mb-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6`}>
              <div className="flex-1">
                 <h2 className="text-lg font-black text-gray-900 dark:text-white leading-tight mb-1">
                    {recommendation}
                 </h2>
                 <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{language === 'lv' ? 'Auto veselības verdikts' : 'Car Health Verdict'}</p>
              </div>
           </div>
        )}

        {/* FINANCIAL SUMMARY SECTION */}
        {!isSimulator && (
           <div className={`bg-white dark:bg-slate-900/60 rounded-[2rem] border border-gray-100 dark:border-white/10 overflow-visible p-0 mb-8 shadow-sm transition-all bg-gradient-to-br from-white to-gray-50/50 dark:from-slate-900 dark:to-slate-900/50`}>
              <div className="px-6 py-4 border-b border-gray-100 dark:border-white/5 bg-white dark:bg-slate-900">
                 <div className="flex items-center gap-2">
                    <Coins size={18} className="text-blue-600" />
                    <h3 className="text-[12px] font-black text-gray-900 dark:text-white uppercase tracking-widest">
                       {language === 'lv' ? 'Finanšu kopsavilkums' : 'Financial Summary'}
                    </h3>
                 </div>
              </div>
              <div className="p-6">
                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="relative">
                       <div className="mb-2">
                          <LabelWithInfo id="total_invested" label={(getFactorConfig('total_invested')?.name as any)[language]} />
                       </div>
                       <div className="text-xl font-black text-gray-900 dark:text-white">{fmt(analysis.totalInvested || 0)}</div>
                       {activeInfo === 'total_invested' && <div className="absolute top-0 left-0 w-full z-20"><InfoDialog id="total_invested" /></div>}
                    </div>
                    <div className="relative">
                       <div className="mb-2">
                          <LabelWithInfo id="investment_gap" label={(getFactorConfig('investment_gap')?.name as any)[language]} />
                       </div>
                       <div className="text-xl font-black text-red-500">{fmt(analysis.investment_gap || analysis.investmentGap || 0)}</div>
                       {activeInfo === 'investment_gap' && <div className="absolute top-0 left-0 w-full z-20"><InfoDialog id="investment_gap" /></div>}
                    </div>
                    <div className="relative">
                       <div className="mb-2">
                          <LabelWithInfo id="monthly_cost" label={(getFactorConfig('monthly_cost')?.name as any)[language]} />
                       </div>
                       <div className="text-xl font-black text-blue-500">{fmt(analysis.monthly_cost_current || 0)}</div>
                       {activeInfo === 'monthly_cost' && <div className="absolute top-0 left-0 w-full z-20"><InfoDialog id="monthly_cost" /></div>}
                    </div>
                 </div>
              </div>
           </div>
        )}
     </>
  );

  return (
    <div className={isSimulator ? "fixed inset-0 z-[60] flex flex-col bg-transparent" : "min-h-screen bg-transparent relative pb-32"}>
      {isSimulator && (
         <div className="relative z-10 flex-shrink-0">
             <NavigationBar 
                title={(t.repairAnalysis as any).title || 'Repair Analysis'}
                leftAction={{
                   icon: Menu,
                   onClick: () => setShowMenu(true),
                   className: "text-blue-600 dark:text-blue-400"
                }}
                fixed={false}
                className="bg-transparent"
               rightContent={
                   <div className="flex items-center bg-blue-600/5 dark:bg-white/5 p-1 rounded-full border border-blue-600/5 dark:border-white/5 shadow-sm animate-in fade-in zoom-in duration-300">
                      <HealthCircle 
                        score={analysis.score} 
                        size={40} 
                        strokeWidth={4} 
                        innerRadius={14} 
                        showText={true} 
                        fontSize="11px" 
                      />
                   </div>
               }
            />
         </div>
      )}
      {/* Global Backdrop for closing popups when clicking outside */}
      {activeInfo && (
         <div 
            className="fixed inset-0 z-[90] bg-transparent"
            onClick={() => setActiveInfo(null)}
         />
      )}

      <div className={isSimulator ? "relative z-10 flex-1 overflow-y-auto no-scrollbar" : "w-full"}>
        <div className={`${compact ? 'px-2' : 'px-4 sm:px-4'} max-w-2xl mx-auto space-y-6 pt-2 pb-32`}>
          
          {/* SIMULATOR QUICK SETTINGS BAR */}
          {isSimulator && (
             <div className="flex items-center gap-2 mb-2 animate-in fade-in slide-in-from-top-4 duration-500">
                {/* Currency Trigger */}
                <button 
                   onClick={() => setShowCurrencyModal(true)}
                   className="flex-1 bg-white dark:bg-slate-900/60 border border-gray-100 dark:border-white/10 rounded-2xl h-12 px-4 flex items-center justify-between shadow-sm hover:border-blue-500/50 transition-colors"
                >
                   <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-lg"><Coins size={14} /></div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">{language === 'lv' ? 'Valūta' : 'Currency'}</span>
                   </div>
                   <span className="text-sm font-black text-gray-900 dark:text-white">{simCurrency}</span>
                </button>

                {/* Distance Toggle */}
                <div className="flex-1 bg-white dark:bg-slate-900/60 border border-gray-100 dark:border-white/10 rounded-2xl h-12 p-1 gap-1 flex items-center shadow-sm">
                   {['miles', 'km'].map(u => (
                      <button 
                         key={u}
                         onClick={() => setSimDistanceUnit(u)}
                         className={`flex-1 h-full rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                            simDistanceUnit === u 
                               ? 'bg-blue-600 text-white shadow-sm' 
                               : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                         }`}
                      >
                         {u}
                      </button>
                   ))}
                </div>
             </div>
          )}

          <SummaryBlocks />

          {/* CONTROL CENTER */}
          <div className={`bg-white dark:bg-slate-900/60 rounded-[2rem] border border-gray-100 dark:border-white/10 overflow-hidden p-0 shadow-sm transition-all mb-12 ${isReadOnly ? 'pointer-events-none' : ''}`}>
             <div className="px-6 py-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between rounded-t-[2rem] bg-white dark:bg-slate-900">
                <div className="flex items-center gap-2">
                   <Settings2 size={18} className="text-blue-600" />
                   <h3 className="text-[14px] font-black text-gray-900 dark:text-white uppercase tracking-widest">
                      {language === 'lv' ? 'Diagnostikas centrs' : 'Diagnosis Center'}
                   </h3>
                </div>
             </div>

             <div className="p-6 md:p-8 bg-white dark:bg-slate-900">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                   {/* Column 1 */}
                   <div className="space-y-10">
                      {/* 0. SIMULATOR BASIC TRAITS - Group 1 */}
                      {isSimulator && (
                         <>
                            <TechSelector 
                               id="fuel_type"
                               label=""
                               value={simFuel}
                               options={['petrol', 'diesel', 'electric', 'hybrid']}
                               icon={Zap}
                               color="text-blue-600"
                               onChange={setSimFuel}
                            />

                            <div className="space-y-4 relative">
                               <div className="flex justify-between items-center">
                                  <LabelWithInfo id="car_year" label={(getFactorConfig('car_year')?.name as any)[language]} />
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">{simYear}</span>
                               </div>
                               {activeInfo === 'car_year' && <InfoDialog id="car_year" />}
                               <input 
                                  type="range" 
                                  min="1990" 
                                  max={new Date().getFullYear()} 
                                  step="1" 
                                  value={simYear} 
                                  onChange={e => setSimYear(Number(e.target.value))} 
                                  className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" 
                               />
                            </div>

                            <div className="space-y-4 relative">
                               <div className="flex justify-between items-center">
                                  <LabelWithInfo id="mileage" label={language === 'lv' ? 'Kopējais nobraukums' : 'Total Mileage'} />
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">{(simMileage ?? 0).toLocaleString()} {isSimulator ? simDistanceUnit : (effectiveCar.distance_unit || 'km')}</span>
                               </div>
                               {activeInfo === 'mileage' && <InfoDialog id="mileage" />}
                               <input 
                                  type="range" 
                                  min="0" 
                                  max="500000" 
                                  step="5000" 
                                  value={simMileage} 
                                  onChange={e => setSimMileage(Number(e.target.value))} 
                                  className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" 
                               />
                            </div>
                         </>
                      )}

                      <div className="relative">
                          <div className="flex justify-between items-center mb-2">
                             <div className="flex items-center gap-2">
                                <LabelWithInfo id="yearly_mileage" label={(getFactorConfig('yearly_mileage')?.name as any)[language]?.replace('{unit}', effectiveCar.distance_unit || 'km')} />
                                {yearlyKm === undefined && !isReadOnly && !isSimulator && <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-400/10 px-1.5 py-0.5 rounded-md uppercase tracking-wider">{language === 'lv' ? 'Auto' : 'Auto'}</span>}
                             </div>
                             <span className="text-xs font-medium text-gray-900 dark:text-white">{(yearlyKm ?? suggestedKM ?? 0).toLocaleString()} {effectiveCar.distance_unit || 'km'}</span>
                          </div>
                          {activeInfo === 'yearly_mileage' && <InfoDialog id="yearly_mileage" />}
                          <input type="range" min="1000" max="60000" step="1000" value={yearlyKm ?? suggestedKM ?? 0} onChange={e => setYearlyKm(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                          {!isReadOnly && !isSimulator && <SuggestedRow val={suggestedKM} current={yearlyKm ?? suggestedKM} label={t.repairAnalysis.calculatedYearlyMileage} onSet={setYearlyKm} />}
                       </div>

                      <div className="relative">
                         <div className="flex justify-between items-center mb-2">
                            <LabelWithInfo id="planned_repairs" label={(getFactorConfig('planned_repairs')?.name as any)[language]} />
                            <span className="text-xs font-medium text-gray-900 dark:text-white">{fmt(plannedCost)}</span>
                         </div>
                         {activeInfo === 'planned_repairs' && <InfoDialog id="planned_repairs" />}
                         <input type="range" min="0" max="15000" step="100" value={plannedCost ?? 0} onChange={e => setPlannedCost(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                      </div>
                   </div>

                   {/* Column 2 */}
                   <div className="space-y-10">
                      {isSimulator && (
                         <>
                            <div className="space-y-4 relative">
                               <div className="flex justify-between items-center">
                                  <LabelWithInfo id="ownership_years" label={(getFactorConfig('ownership_years')?.name as any)[language]} />
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">{simOwnership} {language === 'lv' ? 'gadi' : 'years'}</span>
                               </div>
                               {activeInfo === 'ownership_years' && <InfoDialog id="ownership_years" />}
                               <input type="range" min="0" max="25" step="1" value={simOwnership ?? 0} onChange={e => setSimOwnership(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                            </div>
                         </>
                      )}

                      <div className="relative">
                         <div className="flex justify-between items-center mb-2">
                            <LabelWithInfo id="current_value" label={(getFactorConfig('current_value')?.name as any)[language]} />
                            <span className="text-xs font-medium text-gray-900 dark:text-white">{fmt(currentValue ?? suggestedMarketValue ?? 0)}</span>
                         </div>
                         {activeInfo === 'current_value' && <InfoDialog id="current_value" />}
                         <input type="range" min="500" max="100000" step="500" value={currentValue ?? suggestedMarketValue ?? 0} onChange={e => setCurrentValue(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                         {!isSimulator && !isReadOnly && <SuggestedRow val={suggestedMarketValue} current={currentValue ?? suggestedMarketValue} label={language === 'lv' ? "Atrasts: {val}" : "Detected: {val}"} onSet={setCurrentValue} colorClass="green" />}
                      </div>

                      {isSimulator && (
                         <div className="space-y-4 relative">
                            <div className="flex justify-between items-center">
                               <LabelWithInfo id="repairFreq" label={language === 'lv' ? 'Servisa vizītes 12m' : 'Service Visits 12m'} />
                               <span className="text-xs font-medium text-gray-900 dark:text-white">{simServiceVisits}</span>
                            </div>
                            {activeInfo === 'repairFreq' && <InfoDialog id="repairFreq" />}
                            <input type="range" min="0" max="15" step="1" value={simServiceVisits ?? 0} onChange={e => setSimServiceVisits(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                         </div>
                      )}

                      <div className="relative">
                          <div className="flex justify-between items-center mb-2">
                             <div className="flex items-center gap-2">
                                <LabelWithInfo id="estimated_repairs" label={(getFactorConfig('estimated_repairs')?.name as any)?.[language]} />
                                {yearlyRepairCost === undefined && !isReadOnly && !isSimulator && <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-400/10 px-1.5 py-0.5 rounded-md uppercase tracking-wider">{language === 'lv' ? 'Auto' : 'Auto'}</span>}
                             </div>
                             <span className="text-xs font-medium text-gray-900 dark:text-white">{fmt(yearlyRepairCost ?? suggestedRepairs ?? 0)}</span>
                          </div>
                          {activeInfo === 'estimated_repairs' && <InfoDialog id="estimated_repairs" />}
                          <input type="range" min="0" max="10000" step="100" value={yearlyRepairCost ?? suggestedRepairs ?? 0} onChange={e => setYearlyRepairCost(Number(e.target.value))} className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" />
                          {!isReadOnly && !isSimulator && <SuggestedRow val={suggestedRepairs} current={yearlyRepairCost ?? suggestedRepairs} label={t.repairAnalysis.calculatedYearlyRepairs} onSet={setYearlyRepairCost} colorClass="green" />}
                       </div>
                   </div>
                </div>
             </div>

             {/* Maintenance & DIY (Full Width Container) */}
             <div className="mt-10 pb-10 px-1.5 md:px-2 border-t border-gray-100 dark:border-white/5 bg-gray-50/10 dark:bg-slate-900/10">
                <div className="p-6 space-y-10">
                   <ScoreSelector id="maintenance_discipline" label={(getFactorConfig('maintenance_discipline')?.name as any)[language]} value={maintenanceDiscipline} icon={Wrench} color="text-indigo-500" onChange={setMaintenanceDiscipline} />
                   <ScoreSelector id="diy_repairs" label={(getFactorConfig('diy_repairs')?.name as any)[language]} value={diyRepairs} icon={Wrench} color="text-green-500" onChange={setDiyRepairs} />
                </div>
             </div>
             
             {/* Technical Block */}
             <div className="px-6 py-10 border-t border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-slate-900/50 space-y-10">
                <TechSelector id="rust" label={(getFactorConfig('rust')?.name as any)[language]} value={techRust} icon={Zap} color="text-amber-500" options={['none', 'surface', 'moderate', 'severe']} onChange={setTechRust} />
                <TechSelector id="accident" label={(getFactorConfig('accident')?.name as any)[language]} value={techAccident} icon={AlertTriangle} color="text-red-500" options={['none', 'minor', 'moderate', 'structural']} onChange={setTechAccident} />
                <TechSelector id="repaint" label={(getFactorConfig('repaint')?.name as any)[language]} value={techRepaint} icon={ShieldCheck} color="text-indigo-500" options={['none', 'minor', 'moderate', 'full']} onChange={setTechRepaint} />
                <TechSelector id="unresolved" label={(getFactorConfig('unresolved')?.name as any)[language]} value={techUnresolved} icon={Zap} color="text-orange-600" options={['none', 'minor', 'major']} onChange={setTechUnresolved} />
             </div>

             {/* Replacement Comparison (New Car Plans) */}
             <div className="px-6 py-10 border-t border-gray-100 dark:border-white/5 bg-gray-50/10 dark:bg-slate-900/10 space-y-10">
                <TechSelector 
                   id="replacement_comparison" 
                   label={(getFactorConfig('replacement_comparison')?.name as any)[language]} 
                   value={nextCarPlan} 
                   icon={Globe} 
                   color="text-blue-500" 
                   options={['none', 'used', 'new']} 
                   onChange={setNextCarPlan} 
                />
                {nextCarPlan !== 'none' && (
                   <div className="space-y-4 relative animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex justify-between items-center">
                         <LabelWithInfo id="next_car_budget" label={language === 'lv' ? 'Nākamā auto budžets' : 'Next Car Budget'} />
                         <span className="text-xs font-medium text-gray-900 dark:text-white">{fmt(nextCarBudget)}</span>
                      </div>
                      {activeInfo === 'next_car_budget' && <InfoDialog id="next_car_budget" />}
                      <input 
                         type="range" 
                         min="2000" 
                         max="100000" 
                         step="1000" 
                         value={nextCarBudget || 15000} 
                         onChange={e => setNextCarBudget(Number(e.target.value))} 
                         className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none accent-blue-600" 
                      />
                   </div>
                )}
             </div>

             {/* Survey Questions */}
             <div className="px-6 py-10 border-t border-gray-100 dark:border-white/5 bg-white dark:bg-slate-900 rounded-b-[2rem] space-y-10">
                <ScoreSelector id="practical_fit" label={(getFactorConfig('practical_fit')?.name as any)[language]} value={q1} icon={Check} color="text-green-500" onChange={setQ1} />
                <ScoreSelector id="satisfaction" label={(getFactorConfig('satisfaction')?.name as any)[language]} value={q2} icon={Heart} color="text-red-500" onChange={setQ2} />
                <ScoreSelector id="reliability_anxiety" label={(getFactorConfig('reliability_anxiety')?.name as any)[language]} value={q3} icon={ShieldCheck} color="text-blue-500" onChange={setQ3} />
                <ScoreSelector id="family_priority" label={t.repairAnalysis.q4} value={q4} icon={Globe} color="text-indigo-600" onChange={setQ4} />
             </div>
          </div>

          <div className="mt-12">
             <SummaryBlocks />
          </div>

          {/* ANALYSIS DETAILS */}
          <div className="space-y-6 mt-12">
             <div className="flex items-center px-4">
                <h3 className={theme.typography.sectionTitle}>{language === 'lv' ? 'Analīzes detaļas' : 'Analysis Details'}</h3>
             </div>
             <div className="bg-white dark:bg-slate-900/60 rounded-[2rem] divide-y border-none shadow-sm overflow-visible">
                {analysis.factors.map((factor: any) => (
                   <div key={factor.id} className="flex flex-col p-4 hover:bg-gray-100/[0.02] transition-colors">
                      <div className="flex items-start gap-4">
                         <div className={`p-2.5 rounded-xl shrink-0 ${
                            factor.type === 'negative' ? 'bg-red-50 text-red-500' : 
                            factor.type === 'neutral' ? 'bg-gray-50 text-gray-400' : 
                            'bg-green-50 text-green-500'
                         }`}>
                            {factor.type === 'negative' ? <ThumbsDown size={16} /> : <ThumbsUp size={16} />}
                         </div>
                         <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center mb-0.5">
                               <h5 className="text-[13px] font-black uppercase text-gray-900 dark:text-white truncate">
                                  {(() => {
                                     const cfg = getFactorConfig(factor.id);
                                     let name = (cfg?.name as any)?.[language] || cfg?.name?.en || (t.repairAnalysis.factors as any)?.[factor.name] || (t.repairAnalysis.factors as any)?.[factor.id] || factor.name;
                                     if (factor.details) {
                                        Object.entries(factor.details).forEach(([k, v]) => {
                                           name = name.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
                                        });
                                     }
                                     return name;
                                  })()}
                               </h5>
                               <span className={`text-[11px] font-black ${factor.impact < 0 ? 'text-red-500' : factor.impact === 0 ? 'text-gray-400' : 'text-green-500'}`}>
                                  {factor.impact > 0 ? `+${factor.impact}` : factor.impact}
                               </span>
                            </div>
                            <p className="text-[11px] font-bold text-gray-400 block mb-2">
                               {(() => {
                                  const cfg = getFactorConfig(factor.id);
                                  const sId = factor.id === 'reliability_anxiety' ? 'anxiety' : (factor.id === 'satisfaction' ? 'joy' : (factor.id === 'practical_fit' ? 'practical' : factor.id));
                                  if (['practical', 'joy', 'anxiety', 'practical_fit', 'satisfaction', 'reliability_anxiety'].includes(factor.id)) {
                                     const score = factor.details?.score || parseInt(factor.value);
                                     const meaning = (t.repairAnalysis as any).surveyMeanings?.[sId]?.[score];
                                     if (meaning) return `${score}/5 — ${meaning}`;
                                  }
                                  if (cfg?.meanings && factor.details?.score !== undefined) {
                                     const score = factor.details.score;
                                     const maxScore = Math.max(...Object.keys(cfg.meanings).map(Number));
                                     const meaningObj = cfg.meanings[score] as any;
                                     const meaning = meaningObj ? (meaningObj[language] || meaningObj.en) : '';
                                     return `${score}/${maxScore}${meaning ? ` — ${meaning}` : ''}`;
                                  }
                                  const unitKey = factor.details?.unitTemplate;
                                  if (unitKey && UNIT_TEMPLATES[unitKey]) {
                                     let template = (UNIT_TEMPLATES[unitKey] as any)[language] || UNIT_TEMPLATES[unitKey].en || '';
                                     template = template.replace('{val}', factor.value);
                                     if (factor.details) {
                                         Object.entries(factor.details).forEach(([k, v]) => {
                                             template = template.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
                                         });
                                     }
                                     return template;
                                  }
                                  if (cfg?.valueLabels && factor.value) {
                                     const labelObj = cfg.valueLabels[factor.value] || cfg.valueLabels[factor.value.toLowerCase()];
                                     if (labelObj) {
                                        const label = (labelObj as any)[language] || (labelObj as any).en;
                                        return label === 'none' ? (language === 'lv' ? 'Nav' : 'None') : label;
                                     }
                                  }
                                  if (FUEL_TYPES_CONFIG[factor.value]) return (FUEL_TYPES_CONFIG[factor.value] as any)[language] || FUEL_TYPES_CONFIG[factor.value].en;
                                  const label = (t.repairAnalysis.factors as any)?.[factor.value] || factor.value;
                                  return (label === 'none' || !label) ? (language === 'lv' ? 'Nav' : 'None') : label;
                               })()}
                            </p>
                            <p className="text-[12px] font-medium text-gray-400 border-l-2 border-gray-100 dark:border-white/5 pl-3 leading-relaxed whitespace-pre-wrap">
                               {(() => {
                                  const cfg = getFactorConfig(factor.id);
                                  const factorKey = factor.explain_key || factor.id;
                                  let text = (cfg?.explanation as any)?.[language] || (cfg?.explanation as any)?.en || (t.repairAnalysis as any)?.explanations?.[factorKey];
                                  if (!text) text = (t.repairAnalysis.factors as any)?.[factor.description] || "";
                                  if (factor.details) {
                                      Object.entries(factor.details).forEach(([k, v]) => {
                                         let finalVal = String(v);
                                         if (cfg?.valueLabels) {
                                            const labelObj = cfg.valueLabels[finalVal] || cfg.valueLabels[finalVal.toLowerCase()];
                                            if (labelObj) finalVal = (labelObj as any)[language] || (labelObj as any).en || finalVal;
                                         }
                                         if (FUEL_TYPES_CONFIG[finalVal]) finalVal = (FUEL_TYPES_CONFIG[finalVal] as any)?.[language] || (FUEL_TYPES_CONFIG[finalVal] as any)?.en || finalVal;
                                         if (finalVal.toLowerCase() === 'none') finalVal = (language === 'lv' ? 'nav' : 'none');
                                         text = (text as string).replace(new RegExp(`\\{${k}\\}`, 'g'), finalVal);
                                      });
                                   }
                                  const factorsObj = (t.repairAnalysis.factors as any) || {};
                                  let improveText: string | null = null;
                                  if (factor.adviceKey) {
                                     improveText = factorsObj[factor.adviceKey] || null;
                                     if (!improveText) {
                                        const variant = factor.adviceKey.split('_').pop() as 'high' | 'low' | 'optimal';
                                        improveText = (cfg?.advice?.[variant] as any)?.[language || 'en'] || (cfg?.advice?.[variant] as any).en || null;
                                     }
                                  }
                                  return (
                                     <>
                                        {text}
                                        {factor.impact < 0 && improveText && (
                                           <>
                                              {"\n\n"}
                                              <strong>{factorsObj.improveLabel || "What would improve this:"}</strong> {improveText}
                                           </>
                                        )}
                                     </>
                                  );
                               })()}
                            </p>
                         </div>
                      </div>
                   </div>
                ))}
             </div>
          </div>
        </div>

        {showMenu && (
          <div className="fixed inset-0 z-[100] flex items-start justify-start">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMenu(false)} />
            <div className="relative w-[280px] h-full bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
               <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                  <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">{t.common.menu}</h2>
                  <button onClick={() => setShowMenu(false)} className="text-gray-400 hover:text-red-500 transition-colors"><X size={20} /></button>
               </div>
               <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                  <button onClick={() => { navigate('/landing'); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors">
                     <div className="p-2 bg-blue-50 dark:bg-blue-900/40 text-blue-600 rounded-lg"><Home size={20} /></div>
                     <span className="text-sm font-bold">{t.common.home}</span>
                  </button>
                  <button onClick={() => { setShowLanguageModal(true); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors">
                     <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 rounded-lg"><Globe size={20} /></div>
                     <span className="text-sm font-bold">{t.settings?.language || 'Language'}</span>
                  </button>
                  <button onClick={() => { updatePreferences({ theme: interfacePrefs?.theme === 'light' ? 'dark' : 'light' }); setShowMenu(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors">
                     <div className="p-2 bg-amber-50 dark:bg-amber-900/40 text-amber-600 rounded-lg">
                        {interfacePrefs?.theme === 'light' ? <Sun size={20} /> : <Moon size={20} />}
                     </div>
                     <span className="text-sm font-bold">{t.settings?.interfaceTheme || 'Design'}</span>
                  </button>
               </div>
            </div>
          </div>
        )}

        {/* Feedback Section */}
        <div className="mt-16 pb-12 text-center">
           <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {language === 'lv' ? 'Atsauksmes vai idejas uzlabojumiem?' : 'Feedback or ideas how to improve?'} 
              {' '}
              <a 
                 href="mailto:info@mycarcosts.com" 
                 className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
              >
                 Contact us at info@mycarcosts.com
              </a>
           </p>
        </div>

        {showCurrencyModal && (
           <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCurrencyModal(false)} />
              <div className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in duration-200">
                 <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                    <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-widest">{language === 'lv' ? 'Izvēlieties valūtu' : 'Choose Currency'}</h2>
                    <button onClick={() => setShowCurrencyModal(false)} className="text-gray-400 hover:text-red-500"><X size={24} /></button>
                 </div>
                 <div className="p-4 grid grid-cols-2 gap-2">
                    {['USD', 'EUR', 'GBP', 'PLN', 'SEK', 'NOK', 'CHF'].map(curr => (
                       <button 
                          key={curr}
                          onClick={() => { setSimCurrency(curr); setShowCurrencyModal(false); }}
                          className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                             simCurrency === curr 
                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600' 
                                : 'border-transparent bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                          }`}
                       >
                          <span className="text-xl font-black">{curr}</span>
                       </button>
                    ))}
                 </div>
              </div>
           </div>
        )}

        <LanguagePickerModal 
           isOpen={showLanguageModal}
           title={t.settings?.language || 'Interface Language'}
           currentLanguage={(interfacePrefs?.language as SupportedLanguage) || 'en'}
           onClose={() => setShowLanguageModal(false)}
           onSelect={(lang: SupportedLanguage) => {
              updatePreferences({ language: lang });
              setShowLanguageModal(false);
           }}
        />
      </div>
    </div>
  );
}
